import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectUserInfo,
  selectIsUserAdmin,
  selectIsUserStaff,
  loadUserInfo,
  logoutUser,
} from '../../store/features/user';
import { isTokenValid } from '../../utils/jwt-helper';
import { fetchUserDetails } from '../../api/userInfo';
import { setLoading } from '../../store/features/common';

/**
 * Gate for /admin. Admits ADMIN and (since 2026-06, owner request) STAFF —
 * AdminPanel then registers only the read-only Productos / Pedidos /
 * Categorías / Colecciones resources for staff. Data access is enforced
 * SERVER-side (GETs opened to STAFF; every mutation still ADMIN).
 */
const AdminProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const isUserAdmin = useSelector(selectIsUserAdmin);
  const isUserStaff = useSelector(selectIsUserStaff);
  const location = useLocation();

  const isLoggedIn = isTokenValid();

  // Load user info if not loaded but user has valid token
  useEffect(() => {
    if (isLoggedIn && (!userInfo || !userInfo.id)) {
      dispatch(setLoading(true));
      fetchUserDetails()
        .then((res) => {
          dispatch(loadUserInfo(res));
        })
        .catch((err) => {
          console.error('Failed to load user details:', err);
          const status = err?.response?.status;
          if (status === 401) {
            dispatch(logoutUser());
            window.location.href =
              '/login?redirect=' + encodeURIComponent(window.location.pathname || '/admin');
          }
        })
        .finally(() => {
          dispatch(setLoading(false));
        });
    }
  }, [dispatch, isLoggedIn, userInfo]);

  // If not logged in, redirect to login
  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user info is not loaded yet, show loading or wait
  if (!userInfo || !userInfo.id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div
            className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full"
            role="status"
          >
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Neither admin nor staff → back to the storefront.
  if (!isUserAdmin && !isUserStaff) {
    return <Navigate to="/" replace />;
  }

  // Admin or staff — AdminPanel decides what each role sees.
  return children;
};

export default AdminProtectedRoute;
